import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { useAuth } from '@/hooks/auth/useAuth';
import type { BaseImageView, UpdateBaseImageOverridesRequest } from '@reflex/shared';

/**
 * Per-org base image view. Refetches every 5s while a build is in flight
 * so the operator sees `rebuildPending: true → false` transitions without
 * a manual refresh.
 */
export function useBaseImage() {
  const orgKey = useOrgKey();
  const { activeMembership } = useAuth();
  const organizationId = activeMembership?.organization.id;
  return useQuery({
    queryKey: queryKeys.baseImage(orgKey),
    queryFn: () => api.getBaseImage(organizationId!),
    enabled: !!organizationId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const pending =
        data.rebuildPending ||
        data.currentStatus === 'queued' ||
        data.currentStatus === 'provisioning' ||
        data.currentStatus === 'building';
      return pending ? 5000 : false;
    },
  });
}

/**
 * Force an unconditional base rebuild off the latest public base
 * (`POST /organizations/:id/base-image/rebuild`). The server responds 202
 * and rebuilds in the background — re-pinning the default blueprint id and
 * cascading child rebuilds — so progress surfaces through `useBaseImage`'s
 * polling, not this mutation.
 */
export function useRebuildBaseImage() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  const { activeMembership } = useAuth();
  const organizationId = activeMembership?.organization.id;
  return useMutation({
    mutationFn: () => api.rebuildBaseImage(organizationId!),
    onSuccess: () => {
      // Optimistically flip the cached base image to an in-flight state
      // rather than invalidating (which fires an immediate refetch).
      // A force rebuild creates the new build row in the background AFTER
      // the 202 returns, and because it reproduces an identical command
      // set there is no drift for `rebuildPending` to catch — so an
      // immediate refetch reads the still-`build_complete` previous row,
      // leaving the badge on "Ready" and the button enabled, and
      // `refetchInterval` then stops polling because nothing looks
      // pending. Seeding `queued` here drives the badge/button straight
      // away and keeps polling alive; the first poll lands once the new
      // build row exists and reconciles to the real status.
      qc.setQueryData<BaseImageView>(queryKeys.baseImage(orgKey), (prev) =>
        prev ? { ...prev, currentStatus: 'queued', rebuildPending: true } : prev,
      );
      void qc.invalidateQueries({ queryKey: queryKeys.blueprints(orgKey) });
    },
  });
}

export function useUpdateBaseImageOverrides() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  const { activeMembership } = useAuth();
  const organizationId = activeMembership?.organization.id;
  return useMutation({
    mutationFn: (data: UpdateBaseImageOverridesRequest) =>
      api.updateBaseImageOverrides(organizationId!, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.baseImage(orgKey) });
    },
  });
}
